import { useRef, useState } from "react";

export default function Splitter({
  label,
  value,
  min,
  max,
  onChange,
  orientation = "vertical",
  className = "",
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  orientation?: "vertical" | "horizontal";
  className?: string;
}) {
  const start = useRef({ position: 0, value: 0 });
  const [dragging, setDragging] = useState(false);
  const vertical = orientation === "vertical";
  const clamp = (next: number) => Math.max(min, Math.min(max, next));
  return (
    <div
      className={`splitter ${className}`}
      data-dragging={dragging || undefined}
      role="separator"
      tabIndex={0}
      aria-label={label}
      aria-orientation={orientation}
      aria-valuenow={Math.round(value)}
      aria-valuemin={Math.round(min)}
      aria-valuemax={Math.round(max)}
      aria-valuetext={`${Math.round(value)} 像素`}
      onPointerDown={(event) => {
        if (!event.isPrimary || event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.focus();
        start.current = {
          position: vertical ? event.clientX : event.clientY,
          value,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
      }}
      onPointerMove={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId))
          onChange(
            clamp(
              start.current.value +
                (vertical ? event.clientX : event.clientY) -
                start.current.position,
            ),
          );
      }}
      onPointerUp={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId))
          event.currentTarget.releasePointerCapture(event.pointerId);
        setDragging(false);
      }}
      onPointerCancel={() => setDragging(false)}
      onLostPointerCapture={() => setDragging(false)}
      onKeyDown={(event) => {
        const previous = vertical ? "ArrowLeft" : "ArrowUp";
        const next = vertical ? "ArrowRight" : "ArrowDown";
        if ([previous, next, "Home", "End"].includes(event.key)) {
          event.preventDefault();
          onChange(
            event.key === "Home"
              ? min
              : event.key === "End"
                ? max
                : clamp(value + (event.key === previous ? -10 : 10)),
          );
        }
      }}
    />
  );
}
